import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiDownload, FiExternalLink, FiFilter, FiPackage, FiShoppingBag, FiTruck, FiUsers } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import { exportOpsOrdersCsv, getOpsOrdersByUsers, updateUserDeliveryFulfillment } from "../../../../../api/adminOpsOrders";
import { ComponentLoader } from "../../../../../components/loaders";
import { DashboardHeader, MetricsGrid } from "../../dashboards/dashboard-components";
import { UserOrderTransactionModalGlobalStyle } from "../ops-orders.style";
import { activateNotification } from "../../../../../utils/notifications-utils";
import { DeliveryUpdateModal, TransactionDetailsModal } from "./order-user-modals.section";
import { UserOrderGroup } from "./user-order-group.section";
import {
  FULFILLMENT_STATUSES,
  dateOnly,
  formatOrderStatus as formatStatus,
  getPaginationItems as paginationItems,
  groupUsersByDeliveryDate,
} from "../ops-orders.utils";

const statuses = FULFILLMENT_STATUSES;

export const OpsOrdersUsersSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterMenuRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ total_users: 0, total_orders_count: 0, total_purchased_orders: 0, total_fulfilled_orders: 0, current_page: 1, total_pages: 1, pageSize: 10 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [deliveryUpdate, setDeliveryUpdate] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState("pending");
  const [sendNotification, setSendNotification] = useState(true);
  const [savingDeliveryUpdate, setSavingDeliveryUpdate] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fulfillmentStatus = searchParams.get("fulfillment_status") || "";
  const selectedDeliveryDate = searchParams.get("delivery_date") || "";
  const selectedState = searchParams.get("state") || "";
  const sortBy = searchParams.get("sortBy") || "delivery_date";
  const sortOrder = searchParams.get("sortOrder") || "desc";
  const filters = { fulfillment_status: fulfillmentStatus, delivery_date: selectedDeliveryDate, state: selectedState, sortBy, sortOrder };
  const selectedStatuses = fulfillmentStatus.split(",").filter(Boolean);

  const loadUsers = useCallback(async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const response = await getOpsOrdersByUsers({ page, pageSize: pagination.pageSize, fulfillment_status: fulfillmentStatus, delivery_date: selectedDeliveryDate, state: selectedState, sortBy, sortOrder });
      if (!response.status) throw new Error(response.message || "Orders by users could not be loaded.");
      setUsers(response.data);
      setPagination(response.pagination);
    } catch (requestError) {
      setUsers([]);
      setError(requestError?.response?.data?.message || requestError?.message || "Orders by users could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [fulfillmentStatus, pagination.pageSize, selectedDeliveryDate, selectedState, sortBy, sortOrder]);

  useEffect(() => { loadUsers(Number(searchParams.get("page") || 1)); }, [loadUsers, searchParams]);

  useEffect(() => {
    const closeFilterMenu = (event) => {
      const filterMenu = filterMenuRef.current;
      if (filterMenu?.open && !filterMenu.contains(event.target)) filterMenu.removeAttribute("open");
    };

    document.addEventListener("pointerdown", closeFilterMenu);
    return () => document.removeEventListener("pointerdown", closeFilterMenu);
  }, []);

  const setFilter = (name, value) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(name, value);
      else next.delete(name);
      if (name !== "page") next.set("page", "1");
      return next;
    });
  };
  const toggleFulfilmentStatus = (status) => {
    const nextStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter((item) => item !== status)
      : [...selectedStatuses, status];
    setFilter("fulfillment_status", nextStatuses.join(","));
  };
  const clearFilters = () => setSearchParams({ page: "1" });
  const changePage = (page) => setSearchParams((current) => {
    const next = new URLSearchParams(current);
    next.set("page", String(page));
    return next;
  });
  const groupedUsers = useMemo(() => groupUsersByDeliveryDate(users), [users]);
  const currentPage = pagination.current_page;
  const rangeStart = pagination.total_users ? ((currentPage - 1) * pagination.pageSize) + 1 : 0;
  const rangeEnd = Math.min(currentPage * pagination.pageSize, pagination.total_users);
  const activeFilterCount = [filters.fulfillment_status, filters.delivery_date, filters.state, filters.sortBy !== "delivery_date" || filters.sortOrder !== "asc"].filter(Boolean).length;
  const metrics = [
    { label: "Customers", value: pagination.total_users.toLocaleString(), note: "matching delivery groups", icon: FiUsers, color: "blue" },
    { label: "Customer orders", value: pagination.total_orders_count.toLocaleString(), note: "across all matching users", icon: FiShoppingBag },
    { label: "Purchased orders", value: pagination.total_purchased_orders.toLocaleString(), note: "supplier purchase complete", icon: FiPackage, color: "amber" },
    { label: "Fulfilled orders", value: pagination.total_fulfilled_orders.toLocaleString(), note: "delivered or completed", icon: FiTruck },
  ];
  const openDeliveryUpdate = (user) => {
    setDeliveryStatus(user.overall_fulfillment_status || "pending");
    setSendNotification(true);
    setDeliveryUpdate(user);
  };
  const saveDeliveryUpdate = async () => {
    if (!deliveryUpdate?.user_id || !dateOnly(deliveryUpdate.delivery_date)) return;
    setSavingDeliveryUpdate(true);
    try {
      const response = await updateUserDeliveryFulfillment({
        user_id: deliveryUpdate.user_id,
        delivery_date: dateOnly(deliveryUpdate.delivery_date),
        fulfillment_status: deliveryStatus,
        order_notes: "",
        send_notification: sendNotification,
      });
      setUsers((current) => current.map((user) => user.user_id === deliveryUpdate.user_id && dateOnly(user.delivery_date) === dateOnly(deliveryUpdate.delivery_date) ? {
        ...user,
        overall_fulfillment_status: deliveryStatus,
        items: (user.items || []).map((item) => ({ ...item, fulfillment_status: deliveryStatus })),
      } : user));
      activateNotification({
        title: "Delivery group updated",
        body: response.message || "The delivery group was updated successfully.",
      });
      setDeliveryUpdate(null);
    } catch (requestError) {
      activateNotification({
        title: "Could not update delivery group",
        body: requestError?.response?.data?.message || requestError?.message || "The delivery group could not be updated.",
      });
    } finally {
      setSavingDeliveryUpdate(false);
    }
  };
  const exportOrders = async () => {
    setExporting(true);
    try {
      const { blob, contentDisposition } = await exportOpsOrdersCsv({ page: currentPage, pageSize: pagination.pageSize, ...filters });
      const filename = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]?.replace(/^['"]|['"]$/g, "") || "agrogrid-orders.csv";
      const downloadUrl = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = downloadUrl;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (requestError) {
      activateNotification({ title: "Could not export orders", body: requestError?.response?.data?.message || requestError?.message || "The CSV could not be downloaded." });
    } finally {
      setExporting(false);
    }
  };

  return <>
    <UserOrderTransactionModalGlobalStyle />
    <DashboardHeader eyebrow="Operations workspace" title="Order by users" description="Review each customer’s Split-Buy orders, grouped by their delivery date." showActions={false} />
    <MetricsGrid metrics={metrics} />
    <div className="page-tools">
      <details className="orders-filter-menu" ref={filterMenuRef}>
        <summary><FiFilter aria-hidden="true" /><span>Filters</span>{activeFilterCount > 0 && <strong>{activeFilterCount}</strong>}<FiChevronDown className="filter-chevron" aria-hidden="true" /></summary>
        <div className="orders-filter-panel users-filter-panel">
          <header><div><strong>Filter customer orders</strong><span>Find delivery groups and order stages.</span></div><button type="button" onClick={clearFilters} disabled={!activeFilterCount}>Clear all</button></header>
          <div className="filter-grid">
            <details className="filter-full user-status-filter">
              <summary><span>Fulfilment status</span><strong>{selectedStatuses.length ? `${selectedStatuses.length} selected` : "All statuses"}<FiChevronDown aria-hidden="true" /></strong></summary>
              <div className="user-status-filter-options">
                {statuses.map((status) => <label key={status}><input type="checkbox" checked={selectedStatuses.includes(status)} onChange={() => toggleFulfilmentStatus(status)} /><span>{formatStatus(status)}</span></label>)}
              </div>
            </details>
            <label><span>Delivery date</span><input type="date" value={filters.delivery_date} onChange={(event) => setFilter("delivery_date", event.target.value)} /></label>
            <label><span>State</span><input value={filters.state} placeholder="e.g. Lagos" onChange={(event) => setFilter("state", event.target.value)} /></label>
            <label><span>Sort by</span><select value={filters.sortBy} onChange={(event) => setFilter("sortBy", event.target.value)}><option value="delivery_date">Delivery date</option><option value="created_at">Order date</option><option value="total_amount_paid">Amount paid</option></select></label>
            <label><span>Order</span><select value={filters.sortOrder} onChange={(event) => setFilter("sortOrder", event.target.value)}><option value="asc">Ascending</option><option value="desc">Descending</option></select></label>
          </div>
        </div>
      </details>
      <div className="user-orders-page-actions"><button type="button" className="export-orders-button" onClick={exportOrders} disabled={exporting}>{exporting ? "Exporting…" : <><FiDownload />Export CSV</>}</button><Link className="view-splitbuys-link" to="/admin/internal/operations/orders">View order by Split-Buys <FiExternalLink /></Link></div>
    </div>

    {loading ? <ComponentLoader /> : error ? <section className="content-card"><div className="card-heading"><div><h2>Orders by users could not be loaded</h2><p>{error}</p></div><button type="button" className="action-button" onClick={() => loadUsers(currentPage)}>Try again</button></div></section> : groupedUsers.length === 0 ? <section className="content-card"><div className="card-heading"><div><h2>No customer orders found</h2><p>Try changing the fulfilment, date or state filters.</p></div></div></section> : groupedUsers.map((user) => <UserOrderGroup key={`${user.user_id}-${user.delivery_date || "unscheduled"}`} user={user} onViewTransaction={setSelectedTransaction} onUpdateGroup={openDeliveryUpdate} />)}
    <footer className="pagination"><div className="pagination-copy"><span>Showing</span><strong>{rangeStart}–{rangeEnd}</strong><span>of {pagination.total_users} customers</span></div><nav className="pagination-controls" aria-label="Orders by users pagination"><button type="button" className="page-arrow" disabled={loading || currentPage <= 1} onClick={() => changePage(currentPage - 1)} aria-label="Previous page"><FiChevronLeft /></button>{paginationItems(currentPage, pagination.total_pages).map((item) => typeof item === "number" ? <button type="button" className={`page-number ${item === currentPage ? "active" : ""}`} disabled={loading} onClick={() => changePage(item)} aria-current={item === currentPage ? "page" : undefined} key={item}>{item}</button> : <span className="page-ellipsis" key={item}>…</span>)}<button type="button" className="page-arrow" disabled={loading || currentPage >= pagination.total_pages} onClick={() => changePage(currentPage + 1)} aria-label="Next page"><FiChevronRight /></button></nav></footer>
    <TransactionDetailsModal selectedTransaction={selectedTransaction} onClose={() => setSelectedTransaction(null)} />
    <DeliveryUpdateModal deliveryUpdate={deliveryUpdate} status={deliveryStatus} sendNotification={sendNotification} saving={savingDeliveryUpdate} onClose={() => setDeliveryUpdate(null)} onStatusChange={setDeliveryStatus} onNotificationChange={setSendNotification} onSubmit={saveDeliveryUpdate} />
  </>;
};
